import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from './AuthContext';
import {
  fetchSubscriptionBillingConfig,
  getSubscriptionPaymentProviderMeta,
} from '../utils/astropayService';

const USAGE_KINDS = ['sales', 'invoices', 'purchases', 'collections', 'payments'];
const ZERO_USAGE = USAGE_KINDS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
const ZERO_LIMITS = USAGE_KINDS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

const TrialContext = createContext({});

/**
 * Gastify Subscription & Trial Context
 * -------------------------------------
 * Reads the organization's subscription + trial state and exposes:
 *
 *   Trial (legacy compat):
 *     - daysLeft        : days remaining in trial (0 if expired)
 *     - isTrialing      : true while in trial period
 *     - showWarning     : true when ≤5 trial days remain
 *
 *   Subscription:
 *     - subscriptionStatus : 'trial' | 'active' | 'grace' | 'expired' | 'blocked'
 *     - currentPlanKey     : 'basic' | 'premium' | 'enterprise'
 *     - currentPlan        : full plan object { key, name, price_monthly, ... }
 *     - subscriptionExpiresAt : Date | null
 *     - latestSubscription : most recent subscription record
 *     - paymentUrl         : AstroPay payment URL (if any pending subscription)
 *
 *   Access control:
 *     - isActive      : subscription is active (paid and valid)
 *     - isPending     : payment uploaded, awaiting review
 *     - isBlocked     : hard block by superadmin
 *     - isReadOnly    : subscription expired → user sees data but can't operate
 *     - isGrace       : in grace period (trial ended, before block)
 *     - showGraceWarning : true when in grace period (day 25-30 warning)
 *     - graceDaysLeft : days until read-only kicks in
 *
 *   Actions:
 *     - refreshTrial  : re-fetch org + subscription data
 *
 *   Raw:
 *     - organization  : full org row
 *     - loadingTrial  : still fetching
 */
export const TrialProvider = ({ children }) => {
  const { profile } = useAuth();
  const [org, setOrg] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [latestSubscription, setLatestSubscription] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(true);
  const [usage, setUsage] = useState({ ...ZERO_USAGE, cycle_start: null, cycle_end: null });
  const [userCount, setUserCount] = useState(0);

  const orgId = profile?.organization_id;

  const fetchOrg = useCallback(async () => {
    if (!orgId) {
      setLoadingTrial(false);
      return;
    }
    try {
      // Fetch org
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (orgError) throw orgError;
      setOrg(orgData);

      // Fetch current plan — precio FIJO (no se sobrescribe con pricing por sucursal/usuario)
      const planKey = orgData?.current_plan_key || orgData?.plan || 'basic';
      try {
        const { data: planData } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('key', planKey)
          .eq('is_active', true)
          .single();

        if (planData) {
          setCurrentPlan({
            ...planData,
            monthly_limits: planData.monthly_limits || { ...ZERO_LIMITS },
            max_users: Number(planData.max_users) || 1,
            currency: planData.currency || 'ARS',
          });
        } else {
          setCurrentPlan(null);
        }
      } catch (_) {
        setCurrentPlan(null);
      }

      // Fetch usage del ciclo actual + cantidad de usuarios activos
      try {
        const [usageResponse, userCountResponse] = await Promise.allSettled([
          supabase.rpc('get_org_usage', { p_org_id: orgId }),
          supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId),
        ]);

        if (usageResponse.status === 'fulfilled') {
          const row = Array.isArray(usageResponse.value?.data)
            ? usageResponse.value.data[0]
            : usageResponse.value?.data;
          if (row) {
            setUsage({
              cycle_start: row.cycle_start ? new Date(row.cycle_start) : null,
              cycle_end: row.cycle_end ? new Date(row.cycle_end) : null,
              sales: Number(row.sales) || 0,
              invoices: Number(row.invoices) || 0,
              purchases: Number(row.purchases) || 0,
              collections: Number(row.collections) || 0,
              payments: Number(row.payments) || 0,
            });
          }
        }

        if (userCountResponse.status === 'fulfilled') {
          setUserCount(Number(userCountResponse.value?.count) || 0);
        }
      } catch (usageErr) {
        console.warn('[TrialContext] usage fetch failed:', usageErr?.message || usageErr);
      }

      // Fetch latest subscription
      try {
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (subError) {
          // 403 = RLS policy blocks access; 404 = table doesn't exist yet.
          // Both are non-critical: the app works fine without subscription data.
          if (subError.code !== '42501' && subError.message?.indexOf('403') === -1) {
            console.warn('[TrialContext] subscriptions fetch error:', subError.message);
          }
          setLatestSubscription(null);
        } else {
          setLatestSubscription(subData || null);
        }
      } catch (_) {
        setLatestSubscription(null);
      }

      try {
        const nextBillingConfig = await fetchSubscriptionBillingConfig();
        setBillingConfig(nextBillingConfig);
      } catch (_) {
        const provider = 'mercadopago';
        setBillingConfig({
          subscription_payment_provider: provider,
          provider,
          providerMeta: getSubscriptionPaymentProviderMeta(provider),
        });
      }
    } catch (e) {
      console.error('[TrialContext] fetchOrg error:', e);
    } finally {
      setLoadingTrial(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  // Realtime: si la org cambia (webhook activa, superadmin bloquea, etc.)
  // o se inserta una nueva subscription/payment_receipt, refrescamos sin
  // pedirle al usuario que recargue.
  useEffect(() => {
    if (!orgId) return undefined;

    const channel = supabase
      .channel(`org-${orgId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'organizations', filter: `id=eq.${orgId}` },
        () => fetchOrg(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `organization_id=eq.${orgId}` },
        () => fetchOrg(),
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch { /* noop */ }
    };
  }, [orgId, fetchOrg]);

  // Auto-refresh post-operación: cuando una página dispara este evento,
  // re-fetch del uso (sin tocar org/plan, así es rápido).
  useEffect(() => {
    const handler = () => { fetchOrg(); };
    window.addEventListener('gastify:usage-changed', handler);
    return () => window.removeEventListener('gastify:usage-changed', handler);
  }, [fetchOrg]);

  // ── Derived values ──────────────────────────────────────────────────

  const derived = useMemo(() => {
    const now = new Date();

    // Trial calculations
    const trialEndsAt = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;
    const trialStartsAt = org?.trial_starts_at ? new Date(org.trial_starts_at) : null;
    const msLeft = trialEndsAt ? trialEndsAt - now : 0;
    const daysLeft = trialEndsAt ? Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24))) : 0;

    // Subscription status from DB (preferred) or derived from trial
    const subStatus = org?.subscription_status || org?.status || 'trial';

    // Grace period: day 14 → day 30
    // After trial ends (day 14), user enters grace until day 30
    let graceDaysLeft = 0;
    if (trialStartsAt && (subStatus === 'grace' || subStatus === 'trial')) {
      const day30 = new Date(trialStartsAt.getTime() + 30 * 24 * 60 * 60 * 1000);
      const msGrace = day30 - now;
      graceDaysLeft = Math.max(0, Math.ceil(msGrace / (1000 * 60 * 60 * 24)));
    }

    // For post-subscription grace (active → grace)
    if (org?.subscription_expires_at && subStatus === 'grace') {
      const expiresAt = new Date(org.subscription_expires_at);
      const graceEnd = new Date(expiresAt.getTime() + 16 * 24 * 60 * 60 * 1000);
      const msGrace = graceEnd - now;
      graceDaysLeft = Math.max(0, Math.ceil(msGrace / (1000 * 60 * 60 * 24)));
    }

    // Status flags
    const isTrialing   = subStatus === 'trial' && daysLeft > 0;
    const isActive     = subStatus === 'active';
    const isPending    = subStatus === 'payment_pending' || org?.status === 'payment_pending';
    const isGrace      = subStatus === 'grace';
    const isExpired    = subStatus === 'expired';
    const isBlocked    = subStatus === 'blocked' || org?.status === 'blocked';

    // Read-only: expired subs or DB flag
    const isReadOnly = org?.read_only_mode === true || isExpired;

    // Grace warning: show from day 25 (5 days before block)
    const showGraceWarning = isGrace && graceDaysLeft <= 5 && graceDaysLeft > 0;

    // Trial warning: 5 days before trial ends
    const showWarning = isTrialing && daysLeft <= 5;

    // Payment URL from latest pending subscription
    const paymentUrl = latestSubscription?.status === 'pending'
      ? latestSubscription.payment_url
      : null;

    // Plan key
    const currentPlanKey = org?.current_plan_key || org?.plan || 'basic';

    // Subscription expiration
    const subscriptionExpiresAt = org?.subscription_expires_at
      ? new Date(org.subscription_expires_at)
      : null;

    const billingProvider = billingConfig?.provider || 'mercadopago';
    const billingProviderMeta =
      billingConfig?.providerMeta || getSubscriptionPaymentProviderMeta(billingProvider);

    // ── Límites & uso ──────────────────────────────────────────────
    const limits = currentPlan?.monthly_limits || { ...ZERO_LIMITS };
    const maxUsers = Number(currentPlan?.max_users) || 1;

    const usageMap = USAGE_KINDS.reduce((acc, kind) => {
      const used = Number(usage?.[kind]) || 0;
      const limit = Number(limits?.[kind]) || 0; // 0 = ilimitado
      const reached = limit > 0 && used >= limit;
      const percentage = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
      acc[kind] = {
        used,
        limit,
        unlimited: limit === 0,
        reached,
        percentage,
        remaining: limit > 0 ? Math.max(0, limit - used) : null,
      };
      return acc;
    }, {});

    const usersLimit = {
      used: userCount,
      limit: maxUsers,
      unlimited: maxUsers === 0,
      reached: maxUsers > 0 && userCount >= maxUsers,
      percentage: maxUsers > 0 ? Math.min(100, Math.round((userCount / maxUsers) * 100)) : 0,
      remaining: maxUsers > 0 ? Math.max(0, maxUsers - userCount) : null,
    };

    // Helper: ¿puede ejecutar una operación de esta categoría?
    const canDo = (kind) => {
      if (kind === 'users') return !usersLimit.reached;
      const meta = usageMap[kind];
      if (!meta) return true;
      return !meta.reached;
    };

    return {
      daysLeft,
      isTrialing,
      isActive,
      isPending,
      isGrace,
      isExpired,
      isBlocked,
      isReadOnly,
      showWarning,
      showGraceWarning,
      graceDaysLeft,
      paymentUrl,
      currentPlanKey,
      subscriptionExpiresAt,
      status: subStatus,
      billingProvider,
      billingProviderLabel: billingProviderMeta.label,
      billingProviderMeta,
      // ── Límites por plan ──
      limits,
      maxUsers,
      userCount,
      usage,
      usageMap,
      usersLimit,
      canDo,
    };
  }, [billingConfig, currentPlan, org, latestSubscription, usage, userCount]);

  return (
    <TrialContext.Provider value={{
      ...derived,
      organization: org,
      currentPlan,
      latestSubscription,
      loadingTrial,
      refreshTrial: fetchOrg,
    }}>
      {children}
    </TrialContext.Provider>
  );
};

export const useTrial = () => useContext(TrialContext);
